import { useState } from 'react';
import { Link } from 'react-router-dom';

interface SharedFinalCTAProps {
  title?: string;
  subtitle?: string;
  ctaButtonText?: string;
  serviceLabel?: string;
  formId?: string;
}

export default function SharedFinalCTA({
  title = 'Get a Cleaner, Healthier Workplace Today',
  subtitle = 'Request a free, no-obligation cleaning quote. We respond within 24 hours with a customized plan for your facility.',
  ctaButtonText = 'Submit Quote Request',
  serviceLabel = '',
}: SharedFinalCTAProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d6va6p67ipaugi04lhvg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className="relative py-24 overflow-hidden"
      style={{
        backgroundImage: `url('https://public.readdy.ai/ai/img_res/764a1387b83b32b70771b22a6b5d0716.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 bg-navy/90" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left */}
          <div>
            <div className="inline-flex items-center gap-2 bg-accent/20 border border-accent/30 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 rounded-full bg-accent animate-pulse shrink-0"></span>
              <span className="text-accent text-xs font-body font-semibold uppercase tracking-wider">Free Quote — No Obligation</span>
            </div>
            <h2 className="font-heading font-extrabold text-white text-4xl xl:text-5xl mb-5 leading-tight">{title}</h2>
            <p className="text-white/60 font-body text-lg leading-relaxed mb-8">{subtitle}</p>

            <div className="space-y-4 mb-8">
              {[
                { icon: 'ri-check-double-line', text: 'Response within 24 business hours' },
                { icon: 'ri-check-double-line', text: 'No contracts required to get started' },
                { icon: 'ri-check-double-line', text: 'Customized quote based on your facility' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <i className={`${item.icon} text-accent text-base`}></i>
                  <span className="text-white/70 font-body text-sm">{item.text}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-4">
              <a
                href="tel:+15048183347"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-heading font-bold px-6 py-3 rounded-full transition-all whitespace-nowrap text-sm"
              >
                <i className="ri-phone-fill text-accent"></i>
                (504) 818-3347
              </a>
              <Link
                to="/locations/metairie-la"
                className="inline-flex items-center gap-2 text-accent hover:text-white font-body text-sm transition-colors whitespace-nowrap py-3"
              >
                <i className="ri-map-pin-line"></i>
                Metairie, LA 70003
              </Link>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-3xl p-8">
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 flex items-center justify-center bg-accent/10 rounded-full mx-auto mb-5">
                  <i className="ri-checkbox-circle-line text-accent text-3xl"></i>
                </div>
                <h3 className="font-heading font-bold text-navy text-xl mb-2">Quote Request Sent!</h3>
                <p className="text-gray-500 font-body text-sm">We&apos;ll be in touch within 24 business hours with your custom quote.</p>
              </div>
            ) : (
              <>
                <h3 className="font-heading font-extrabold text-navy text-xl mb-1">Get Your Free Cleaning Quote</h3>
                <p className="text-gray-400 font-body text-xs mb-6">Fill out the form — we respond within 24 hours.</p>
                <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="full_name"
                      required
                      placeholder="Full Name *"
                      className="border border-gray-200 rounded-xl px-4 py-3 text-sm font-body text-navy placeholder-gray-400 focus:outline-none focus:border-accent transition-colors w-full"
                    />
                    <input
                      type="text"
                      name="company_name"
                      placeholder="Company Name"
                      className="border border-gray-200 rounded-xl px-4 py-3 text-sm font-body text-navy placeholder-gray-400 focus:outline-none focus:border-accent transition-colors w-full"
                    />
                  </div>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="Email Address *"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm font-body text-navy placeholder-gray-400 focus:outline-none focus:border-accent transition-colors"
                  />
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder="Phone Number *"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm font-body text-navy placeholder-gray-400 focus:outline-none focus:border-accent transition-colors"
                  />
                  {serviceLabel && (
                    <input type="hidden" name="service_needed" value={serviceLabel} />
                  )}
                  <textarea
                    name="message"
                    rows={3}
                    maxLength={500}
                    placeholder="Describe your facility and cleaning needs..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm font-body text-navy placeholder-gray-400 focus:outline-none focus:border-accent transition-colors resize-none"
                  />
                  <p className="text-gray-300 text-xs font-body text-right">{charCount}/500</p>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-accent hover:bg-accent-dark text-navy font-heading font-bold py-3.5 rounded-full transition-all duration-200 text-sm whitespace-nowrap cursor-pointer disabled:opacity-60"
                  >
                    {submitting ? 'Sending...' : ctaButtonText + ' →'}
                  </button>
                  <p className="text-gray-300 text-xs font-body text-center">
                    No commitment required. Your info is kept private.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
